import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input,
  OnChanges, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpErrorResponse} from '@angular/common/http';
import {FormsModule, NgModel} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {A11yModule} from '@angular/cdk/a11y';
import {catchError, debounceTime, of, Subject, switchMap} from 'rxjs';
import {UserRoleService} from '../../../services/userRole.service';
import {UserAuthoritiesEnum} from '../../../shared/model/user.model';
import {CostCalculationService} from '../../../services/cost-calculation.service';
import {
  CostCalculationSettings, DEFAULT_COST_CALCULATION_SETTINGS,
} from '../../../shared/model/cost-calculation.model';
import {MessageService} from '../../../shared/components/message.service';

/** Euro-Betraege in halben Schritten; 1000 als sinnvolle Obergrenze (NP20-932). */
const PARAMETER_MIN = 0;
const PARAMETER_MAX = 1000;
const PARAMETER_STEP = 0.5;

/** Sammelt schnelle Eingaben zu genau einem PUT (NP20-933). */
const SAVE_DEBOUNCE_MS = 400;

export interface CostParameters {
  margin: number | null;
  overhead: number | null;
  travel: number | null;
}

const FIELDS: (keyof CostParameters)[] = ['margin', 'overhead', 'travel'];

function toParameters(settings: CostCalculationSettings): CostParameters {
  return {
    margin: settings.marginEuro,
    overhead: settings.adminOverheadEuro,
    travel: settings.travelExpenseHourly,
  };
}

/** Das Backend haelt zwei Nachkommastellen; mehr wuerde es ohnehin runden. */
function rounded(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Zentrale Parameter der Satzberechnung.
 *
 * Aus dem Kostensatz eines Beraters und diesen drei Werten ergeben sich die
 * Stundensaetze:
 *   Remote  = Kostensatz + Marge + Overhead
 *   vor Ort = Remote + Reisekostensatz
 *
 * Die Werte liegen firmenweit im Backend (NP20-933): beim Oeffnen werden sie
 * geladen, Admins schreiben Aenderungen mit kurzer Verzoegerung zurueck, Office
 * sieht sie read-only. Die berechneten Saetze werden in NP20-934 am Angebot
 * vorbelegt, nicht hier angezeigt.
 */
@Component({
  selector: 'app-cost-calculation-modal',
  templateUrl: './cost-calculation-modal.component.html',
  styleUrl: './cost-calculation-modal.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslatePipe, A11yModule],
})
export class CostCalculationModalComponent implements OnChanges {
  private readonly userRoleService = inject(UserRoleService);
  private readonly costCalculationService = inject(CostCalculationService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @ViewChild('marginField') private readonly marginField?: NgModel;
  @ViewChild('overheadField') private readonly overheadField?: NgModel;
  @ViewChild('travelField') private readonly travelField?: NgModel;

  readonly min = PARAMETER_MIN;
  readonly max = PARAMETER_MAX;
  readonly step = PARAMETER_STEP;

  parameters: CostParameters = toParameters(DEFAULT_COST_CALCULATION_SETTINGS);
  isLoading = false;
  loadFailed = false;

  /** Letzter bestaetigter Stand — Ziel des Rollbacks, wenn ein PUT mit 403 endet. */
  private lastPersisted: CostParameters = {...this.parameters};
  private hasPendingSave = false;
  private readonly saveTrigger = new Subject<void>();
  private readonly openTrigger = new Subject<void>();

  constructor() {
    this.saveTrigger
      .pipe(debounceTime(SAVE_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.flushPendingSave());

    // Schliessen zerstoert die Komponente nicht, ein laufender GET liefe also
    // weiter. `switchMap` bricht ihn beim naechsten Oeffnen ab: sonst koennte die
    // aeltere Antwort den Ladezustand vorzeitig beenden und spaeter Eingaben
    // ueberschreiben.
    this.openTrigger
      .pipe(
        switchMap(() => this.costCalculationService.getSettings().pipe(
          catchError(() => {
            this.loadFailed = true;
            return of(DEFAULT_COST_CALCULATION_SETTINGS);
          }),
        )),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(settings => {
        this.applySettings(settings);
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.load();
    }
  }

  /** Nur Admins pflegen die Werte; Office sieht sie read-only. */
  get editable(): boolean {
    return this.userRoleService.hasRole(UserAuthoritiesEnum.ADMIN);
  }

  get margin(): number {
    return this.amount(this.parameters.margin);
  }

  get overhead(): number {
    return this.amount(this.parameters.overhead);
  }

  get travel(): number {
    return this.amount(this.parameters.travel);
  }

  /** Ein Wert ausserhalb von 0…1000 wuerde das Backend mit 400 ablehnen. */
  isOutOfRange(field: keyof CostParameters): boolean {
    const value = this.parameters[field];
    return typeof value === 'number' && Number.isFinite(value)
      && (value < PARAMETER_MIN || value > PARAMETER_MAX);
  }

  get hasOutOfRange(): boolean {
    return FIELDS.some(field => this.isOutOfRange(field));
  }

  onParameterChange(): void {
    if (!this.editable) return;
    // Erst gar nicht senden, sonst kaeme ein 400 als unspezifischer Speicherfehler zurueck.
    if (this.hasOutOfRange) {
      this.hasPendingSave = false;
      return;
    }
    this.hasPendingSave = true;
    this.saveTrigger.next();
  }

  close(): void {
    this.flushPendingSave();
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onOverlayClick(): void {
    this.close();
  }

  private load(): void {
    this.isLoading = true;
    this.loadFailed = false;
    this.hasPendingSave = false;
    this.openTrigger.next();
  }

  /** Schreibt einen noch ausstehenden Debounce sofort weg, etwa beim Schliessen. */
  private flushPendingSave(): void {
    if (!this.hasPendingSave) return;
    this.hasPendingSave = false;
    this.save();
  }

  private save(): void {
    const settings = this.currentSettings();
    this.costCalculationService.saveSettings(settings)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: saved => {
          this.lastPersisted = toParameters(saved ?? settings);
        },
        error: (err: unknown) => this.handleSaveError(err),
      });
  }

  /** 403 bedeutet, dass die Rolle nicht schreiben darf — dann gilt wieder der Serverstand. */
  private handleSaveError(err: unknown): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    if (status === 403) {
      this.rollbackToPersisted();
      this.messageService.error('adminDash.costCalculation.permissionError');
    } else if (status === 400) {
      // Sollte die Feldpruefung nicht greifen, bleibt die Meldung trotzdem konkret.
      this.messageService.error('adminDash.costCalculation.rangeError');
    } else {
      this.messageService.error('adminDash.costCalculation.saveError');
    }
    this.cdr.markForCheck();
  }

  /**
   * Der zurueckgesetzte Wert entspricht dem zuletzt gebundenen, deshalb sieht die
   * Change Detection keine Aenderung und das Feld zeigt weiter die Eingabe. Der
   * Rollback laeuft daher zusaetzlich ueber das Control — ohne `ngModelChange`,
   * damit daraus kein neuer Speicherversuch entsteht.
   */
  private rollbackToPersisted(): void {
    this.parameters = {...this.lastPersisted};
    const options = {emitViewToModelChange: false};
    this.marginField?.control.setValue(this.parameters.margin, options);
    this.overheadField?.control.setValue(this.parameters.overhead, options);
    this.travelField?.control.setValue(this.parameters.travel, options);
  }

  private applySettings(settings: CostCalculationSettings): void {
    this.parameters = toParameters(settings);
    this.lastPersisted = {...this.parameters};
  }

  private currentSettings(): CostCalculationSettings {
    return {
      marginEuro: rounded(this.margin),
      adminOverheadEuro: rounded(this.overhead),
      travelExpenseHourly: rounded(this.travel),
    };
  }

  /**
   * Leere, negative oder unlesbare Eingaben zaehlen als 0, damit die Formelzeile
   * nie `NaN` zeigt. Ein leeres `type="number"`-Feld liefert `null`.
   */
  private amount(value: number | null | undefined): number {
    return typeof value === 'number' && Number.isFinite(value) && value >= PARAMETER_MIN
      ? value
      : 0;
  }
}
